/**
 * BLOCK 97 ,  User Management: server-side search, sort, pagination, and a
 * real server-computed summary header. The full account set is never loaded
 * into the DOM at once; the table is paginated (25/page) and every search /
 * sort / page change round-trips to the backend so results reflect the true
 * dataset, not a partial client-side slice.
 */
import { useEffect, useState, useCallback } from "react";
import {
  Plus, Trash2, Users as UsersIcon, Search, ArrowUp, ArrowDown, ArrowUpDown,
  ChevronLeft, ChevronRight, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { tierLabel } from "@/lib/tiers";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const ROLES = ["super_admin", "admin", "manager", "specialist", "investor"];
const TIERS = ["free", "pro", "business"];
const PAGE_SIZE = 25;

// BLOCK 169 ,  "business" is a display-only alias; the backend's canonical
// wire value is "enterprise" (TIERS Literal never accepted "business" , 
// sending it 422'd). Translate in both directions right at the edge.
const tierToWire = (uiValue) => (uiValue === "business" ? "enterprise" : uiValue);
const tierToDisplay = (wireValue) => (wireValue === "enterprise" ? "business" : (wireValue || "free"));

// BLOCK 98 ,  normalize any error into a readable STRING. The backend returns
// object-shaped details for MFA gates ({error, message}); passing those raw to
// toast.error() crashes React ("Objects are not valid as a React child").
function errMsg(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (typeof d === "object") return d.message || d.error || fallback;
  return fallback;
}

const SORT_COLS = [
  { key: "name", label: "User" },
  { key: "role", label: "Role" },
  { key: "tier", label: "Tier" },
  { key: "mfa", label: "MFA" },
  { key: "state", label: "State" },
];

export default function UserManagement() {
  return (
    <ErrorBoundary section="User Management">
      <UserManagementInner />
    </ErrorBoundary>
  );
}

function UserManagementInner() {
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, total_pages: 1 });
  const [summary, setSummary] = useState(null);
  const [show, setShow] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Debounce the search box into the actual query param.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setQ(searchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        q, sort_by: sortBy, sort_dir: sortDir, page: String(page), page_size: String(PAGE_SIZE),
      });
      const r = await api.get(`/admin/users?${params.toString()}`);
      setUsers(r.data.users || []);
      setMeta({ total: r.data.total, page: r.data.page, total_pages: r.data.total_pages });
    } catch (e) {
      toast.error(errMsg(e, "Failed to load users"));
    } finally {
      setLoading(false);
    }
  }, [q, sortBy, sortDir, page]);

  const loadSummary = useCallback(() => {
    api.get("/admin/users/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const refresh = () => { load(); loadSummary(); };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key); setSortDir("asc");
    }
    setPage(1);
  };

  const updateUser = async (uid, patch) => {
    try {
      await api.patch(`/admin/users/${uid}`, patch);
      toast.success("Updated");
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.error : null;
      // Role changes are step-up gated. Instead of erroring, prompt for a
      // fresh authenticator code, then retry the change so it actually persists.
      if (code === "mfa_step_up_required") {
        const c = window.prompt(
          "Changing a role is a protected action.\nEnter your current 6-digit authenticator code to authorize:"
        );
        if (!c) { toast.message("Role change cancelled"); refresh(); return; }
        try {
          await api.post("/mfa/step-up", { code: c.trim() });
          await api.patch(`/admin/users/${uid}`, patch);
          toast.success("Role updated");
        } catch (e2) {
          toast.error(errMsg(e2, "Authorization failed , role not changed"));
        }
        refresh();
        return;
      }
      if (code === "mfa_enrollment_required") {
        toast.error("Admin accounts must enroll MFA (Settings , Security) before changing roles.");
        refresh();
        return;
      }
      toast.error(errMsg(e, "Update failed"));
      refresh();
    }
  };

  const removeUser = async (uid) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    }
  };

  const resetMfa = async (u) => {
    const code = window.prompt(
      `Reset MFA for ${u.email}?\n\nThis forces them to re-enroll on next login.\nEnter YOUR current authenticator code to authorize:`
    );
    if (!code) return;
    const reason = window.prompt("Reason (logged to audit trail):") || "";
    try {
      await api.post("/mfa/step-up", { code: code.trim() });
    } catch (e) {
      toast.error(errMsg(e, "Step-up failed , check your code"));
      return;
    }
    try {
      await api.post(`/admin/users/${u.uid}/mfa/reset`, { reason });
      toast.success(`MFA reset for ${u.email} , they must re-enroll on next login`);
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Reset failed"));
    }
  };

  const from = meta.total === 0 ? 0 : (meta.page - 1) * PAGE_SIZE + 1;
  const to = Math.min(meta.page * PAGE_SIZE, meta.total);

  return (
    <div className="space-y-6" data-testid="user-management-view">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Module 5</div>
          <h1 className="font-heading text-4xl tracking-tight">User Management</h1>
          <p className="text-sm text-slate-400 mt-1" data-testid="user-total-label">
            {summary ? `${summary.total} accounts` : "…"} · admin-only
          </p>
        </div>
        <button onClick={() => setShow(true)} data-testid="new-user-btn" className="btn-primary text-sm inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> Invite user
        </button>
      </header>

      <SummaryHeader summary={summary} />

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input
          data-testid="user-search-input"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name or email…"
          className="w-full bg-[#0A0D12] border border-white/10 rounded-sm pl-9 pr-3 py-2 text-sm outline-none focus:border-emerald-500/40"
        />
      </div>

      <div className="ae-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
              {SORT_COLS.map((c) => (
                <th key={c.key} className="text-left px-4 py-3">
                  <button
                    type="button"
                    onClick={() => toggleSort(c.key)}
                    data-testid={`sort-${c.key}`}
                    className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-slate-200 transition-colors"
                  >
                    {c.label}
                    {sortBy === c.key
                      ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3 text-emerald-400" /> : <ArrowDown className="h-3 w-3 text-emerald-400" />)
                      : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                  </button>
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody data-testid="user-table-body">
            {users.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-slate-500 py-12" data-testid="user-empty-state">
                <UsersIcon className="h-8 w-8 mx-auto text-slate-700 mb-2" />
                {loading ? "Loading…" : q ? `No accounts match "${q}"` : "No users yet"}
              </td></tr>
            ) : users.map((u) => (
              <tr key={u.uid} data-testid={`user-row-${u.uid}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-[11px] text-slate-500">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  {ROLES.includes(u.role) ? (
                    <select data-testid={`user-role-${u.uid}`} value={u.role} onChange={(e) => updateUser(u.uid, { role: e.target.value })} className="bg-[#0A0D12] border border-white/10 rounded-sm px-2 py-1 text-xs">
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <span
                      data-testid={`user-role-badge-${u.uid}`}
                      title="Managed exclusively through the submit-then-claim flow, not editable here"
                      className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-mono uppercase tracking-wide border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                    >
                      {u.role}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`user-tier-${u.uid}`}
                    value={tierToDisplay(u.tier)}
                    onChange={(e) => updateUser(u.uid, { tier: tierToWire(e.target.value) })}
                    className="bg-[#0A0D12] border border-white/10 rounded-sm px-2 py-1 text-xs uppercase font-mono"
                  >
                    {TIERS.map((t) => <option key={t} value={t}>{tierLabel(t)}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs">{u.mfa_enabled ? <span className="text-emerald-400">Yes</span> : <span className="text-slate-500">No</span>}</td>
                <td className="px-4 py-3 text-xs">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={u.active !== false} onChange={(e) => updateUser(u.uid, { active: e.target.checked })} className="accent-emerald-500" data-testid={`user-active-${u.uid}`} />
                    <span>{u.active !== false ? "Active" : "Inactive"}</span>
                  </label>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {["admin", "super_admin", "manager"].includes(u.role) && (
                      <button onClick={() => resetMfa(u)} data-testid={`reset-mfa-${u.uid}`} title="Reset this admin's MFA (forces re-enrollment)" className="text-amber-400 hover:bg-amber-500/10 p-1.5 rounded-sm text-xs font-medium">
                        Reset MFA
                      </button>
                    )}
                    <button onClick={() => removeUser(u.uid)} data-testid={`delete-user-${u.uid}`} className="text-rose-400 hover:bg-rose-500/10 p-1.5 rounded-sm">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/8 text-xs text-slate-400">
          <div data-testid="users-range-label">
            {from}-{to} of {meta.total}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={meta.page <= 1}
              data-testid="users-prev-page"
              className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span data-testid="users-page-indicator" className="font-mono">
              Page {meta.page} / {meta.total_pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(meta.total_pages, p + 1))}
              disabled={meta.page >= meta.total_pages}
              data-testid="users-next-page"
              className="btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {show && <NewUserDialog onClose={() => setShow(false)} onCreated={() => { setShow(false); refresh(); }} />}
    </div>
  );
}

function SummaryHeader({ summary }) {
  if (!summary) return null;
  return (
    <div className="ae-card p-5" data-testid="user-summary-header">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total" value={summary.total} testid="user-summary-total" />
        <StatCard label="Active" value={summary.active} testid="user-summary-active" accent="text-emerald-400" />
        <StatCard label="Inactive" value={summary.inactive} testid="user-summary-inactive" accent="text-slate-400" />
        <StatCard label="MFA enabled" value={summary.mfa_enabled} testid="user-summary-mfa-on" accent="text-emerald-400" icon />
        <StatCard label="MFA off" value={summary.mfa_disabled} testid="user-summary-mfa-off" accent="text-amber-400" />
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Breakdown title="By role" data={summary.by_role} prefix="summary-role" />
        <Breakdown title="By tier" data={summary.by_tier} prefix="summary-tier" transform={tierLabel} />
      </div>
    </div>
  );
}

function StatCard({ label, value, testid, accent = "text-slate-100", icon }) {
  return (
    <div className="rounded-sm border border-white/10 bg-[#0A0D12] px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
        {icon && <ShieldCheck className="h-3 w-3 text-emerald-400" />} {label}
      </div>
      <div className={`font-mono tabular-nums text-2xl mt-1 ${accent}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function Breakdown({ title, data, prefix, transform }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      <div className="flex flex-wrap gap-2">
        {entries.map(([k, v]) => (
          <span
            key={k}
            data-testid={`${prefix}-${k}`}
            className="inline-flex items-center gap-1.5 rounded-sm border border-white/10 bg-[#0A0D12] px-2.5 py-1 text-xs"
          >
            <span className="uppercase tracking-wide text-slate-300 font-mono">{transform ? transform(k) : k}</span>
            <span className="font-mono tabular-nums text-emerald-400">{v}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function NewUserDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "specialist", tier: "free" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", { ...f, tier: tierToWire(f.tier) });
      toast.success("User created");
      onCreated();
    } catch (e) {
      toast.error(errMsg(e, "Failed to create user"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
      <form onSubmit={submit} className="ae-card p-6 max-w-md w-full" data-testid="new-user-dialog">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400">Invite User</div>
        <h2 className="font-heading text-2xl tracking-tight mt-1">Create account</h2>
        <div className="space-y-3 mt-4 text-sm">
          <input data-testid="new-user-name" required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="inp" />
          <input data-testid="new-user-email" required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="inp" />
          <input data-testid="new-user-password" required type="password" placeholder="Temporary password (min 6)" minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="inp" />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="new-user-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="inp">{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select>
            <select data-testid="new-user-tier" value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })} className="inp">{TIERS.map((t) => <option key={t} value={t}>{tierLabel(t)}</option>)}</select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="new-user-submit" className="btn-primary text-sm">{busy ? "Creating…" : "Create"}</button>
        </div>
        <style>{`.inp { width: 100%; background: #0A0D12; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; padding: 0.5rem 0.625rem; font-size: 0.875rem; color: #F8FAFC; outline: none; }`}</style>
      </form>
    </div>
  );
}
